import { roundToNearestMinutes, subDays } from "date-fns";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { $streemApiV1 } from "shared/boot/api";
import { type BasePaginationData } from "shared/components/base/BasePagination";
import { type OutreachContactId } from "shared/composables/useContact";
import DateRange from "shared/helpers/DateRange";
import { type MediumName } from "shared/helpers/media";
import { type Source } from "shared/helpers/sourceFilters";
import ModalService from "shared/modals/ModalService";
import { type AIAssistantOption } from "shared/modules/AI/composables/useAIAssistantOptions";
import AIUserContextService from "shared/modules/AI/services/AIUserContextService";
import ApiClient from "shared/services/api/ApiClient";
import features from "shared/services/features";
import { type Stream } from "shared/types";
import { type Nullable } from "shared/types/utility";

export interface AIRegion {
  id: number;
  name: string;
  provider_region: string;
}

export interface AISummarySelectedTopic {
  assistant?: boolean;
  topicId: number;
}

export interface AISummaryResultTopic {
  createdAt: string;
  id: number;
  mentionsCount: number;
  position: number;
  summary: string;
  title: string;
  updatedAt: string;
}

export interface AISummaryResult {
  aiFeatureConfigurationId: number;
  archivedAt: string;
  createdAt: string;
  id: number;
  organisationId: number;
  streamId: number;
  title?: string;
  topics: AISummaryResultTopic[];
}

export interface AISummary {
  dateRange: InstanceType<typeof DateRange>;
  dateRangeUpdated?: boolean;
  error: boolean;
  feedbackId?: number;
  loading: boolean;
  result: Nullable<AISummaryResult>;
  status?: Nullable<number>;
  thumbsUp?: Nullable<boolean>;
}

interface AISummaryTimers {
  [key: number]: Nullable<AbortController>;
}

interface AISummaries {
  [key: number]: AISummary;
}

export interface AISuggestedContactsFilter {
  suggestionsForContact?: OutreachContactId;
  suggestionsForContactListId?: number;
  suggestionsForMediaReleaseId?: string;
}

export interface AIInputItemStream {
  label: string;
  stream: Stream;
  type: "stream";
}

export interface AIInputItemRange {
  label: string;
  range: DateRange;
  type: "range";
}

export interface AIInputItemSource {
  label: string;
  source: Source;
  type: "source";
}

export type AIInputItem =
  | AIInputItemStream
  | AIInputItemRange
  | AIInputItemSource;
export interface AIInput {
  fieldFilter: string;
  fieldFocus: boolean;
  fieldMedia: MediumName[];
  fieldType: Nullable<AIInputItem["type"]>;
  itemIndex: number;
  items: AIInputItem[];
  itemsError: string;
  itemsLoading: boolean;
  open: boolean;
  range: Nullable<DateRange>;
  source: Nullable<Source>;
  stream: Nullable<Stream>;
  totalItems: number;
}

export type AIInputListItem = AIInputItem & {
  active: boolean;
  selected: boolean;
};

export type AIPanelType =
  | "AIAssistantPanel"
  | "AIStreamingAgentTestPanel"
  | "AIBooleanCreatorPanel"
  | "AIChatHistoryListPanel"
  | "AIChatHistoryPanel"
  | "AIContentAnalysisPanel"
  | "AIGeneralAssistantPanel"
  | "AIMentionStreamsAssistantPanel"
  | "AISuggestedContactsAssistantPanel"
  | "AISuggestedContactsPanel"
  | "AISummaryAssistantPanel"
  | "AISummaryClusterPanel"
  | "AISummaryPanel";

export const aiSumaryTimers: AISummaryTimers = {};

export const CHAT_HISTORY_PAGE_SIZE = 50;

export function isAIInputStreamOption(
  item: AIInputItem
): item is AIInputItemStream {
  return item.type === "stream";
}

export function isAIInputRangeOption(
  item: AIInputItem
): item is AIInputItemRange {
  return item.type === "range";
}

export function isAIInputSourceOption(
  item: AIInputItem
): item is AIInputItemSource {
  return item.type === "source";
}

function defaultAIInputOptions(): AIInput {
  return {
    fieldFilter: "",
    fieldFocus: false,
    fieldMedia: [],
    fieldType: null,
    itemIndex: 0,
    items: [],
    itemsError: "",
    itemsLoading: false,
    open: false,
    range: null,
    source: null,
    stream: null,
    totalItems: 0,
  };
}

export const useAIStore = defineStore("ai", () => {
  const aiBooleanCreatorQueryEditorValue = ref<Nullable<string>>(null);
  const aiSummaries = ref<AISummaries>({});
  const aiSummaryStreamId = ref<Nullable<number>>(null);
  const aiModalOpen = ref<boolean>(false);
  const aiModalMinimise = ref<boolean>(false);
  const aiModalLeft = ref<boolean>(false);
  const aiRegions = ref<AIRegion[]>([]);
  const aiSummarySelectedTopic = ref<Nullable<AISummarySelectedTopic>>(null);
  const aiSuggestedContactIds = ref<OutreachContactId[]>([]);
  const aiSuggestedContactsFilter = ref<AISuggestedContactsFilter>({});
  const aiChatHistoryGroupId = ref<Nullable<number>>(null);
  const aiAssistantPrompt = ref<Nullable<AIAssistantOption>>(null);
  const aiSummaryChatHistory = ref<Nullable<AISummary>>(null);
  const aiUserContextEnabled = ref<boolean>(false);
  const aiUserContextLoaded = ref<boolean>(false);
  const aiUserContextLoading = ref<boolean>(false);

  const aiInput = ref<AIInput>(defaultAIInputOptions());

  const aiInputListItems = computed<AIInputListItem[]>(() =>
    aiInput.value.items.map((item, itemIndex) => {
      let selected = false;

      if (isAIInputSourceOption(item)) {
        selected = aiInput.value.source?.id === item.source.id;
      } else if (isAIInputStreamOption(item)) {
        selected = aiInput.value.stream?.id === item.stream.id;
      } else if (isAIInputRangeOption(item)) {
        selected = item.range.toString() === aiInput.value.range?.toString();
      }

      const active = !selected && aiInput.value.itemIndex === itemIndex;

      return { ...item, active, selected };
    })
  );

  const aiChatHistoryScrollPosition = ref<number>(0);

  const aiChatHistoryPagination = ref<BasePaginationData>({
    page: 1,
    pageCount: 0,
    pageSize: CHAT_HISTORY_PAGE_SIZE,
  });

  const aiModalPanel = ref<AIPanelType>("AIAssistantPanel");

  const aiStreamSummary = computed<AISummary>(() => {
    if (aiSummaryStreamId.value) {
      return getAISummary(aiSummaryStreamId.value);
    }

    return {
      dateRange: DateRange.last24Hours(),
      error: false,
      loading: false,
      result: null,
    };
  });

  function setAISummary(streamId: number, summary: Partial<AISummary>): void {
    aiSummaries.value[streamId] = {
      ...aiSummaries.value[streamId],
      ...summary,
    };
  }

  function getAISummary(streamId: number): Required<AISummary> {
    const before = roundToNearestMinutes(new Date(), { nearestTo: 15 });
    const after = subDays(before, 1);
    const dateRange = DateRange.fromDates(after, before);

    return {
      dateRange,
      dateRangeUpdated: false,
      error: false,
      feedbackId: 0,
      loading: false,
      result: null,
      status: null,
      thumbsUp: null,
      ...(aiSummaries.value[streamId] as Partial<AISummary>),
    };
  }

  async function loadAISummary(
    streamId: number,
    beta?: boolean
  ): Promise<void> {
    const summary = getAISummary(streamId);
    if (summary.loading) return;

    setAISummary(streamId, {
      error: false,
      feedbackId: 0,
      loading: true,
      result: null,
      status: null,
      thumbsUp: null,
    });

    try {
      const abortController = new AbortController();
      aiSumaryTimers[streamId] = abortController;

      const { data, status } = await $streemApiV1.post<AISummaryResult>(
        "ai/summarisations",
        {
          params: {
            contentSummarisation: {
              after: summary.dateRange.after,
              before: summary.dateRange.before,
              beta,
              pageNumber: 1,
              pageSize: 1000,
              streamId,
            },
          },
          signal: abortController.signal,
          transformKeys: true,
        }
      );

      setAISummary(streamId, {
        error: false,
        loading: false,
        result: data,
        status,
      });
    } catch (error: unknown) {
      let status: number = 500;

      if (ApiClient.isApiError(error) && error.response?.status) {
        status = error.response?.status || 500;
      }

      setAISummary(streamId, {
        error: true,
        loading: false,
        result: null,
        status: Number(status),
      });
    }
  }

  function abortAISummary(streamId: number): void {
    if (aiSumaryTimers[streamId]) {
      aiSumaryTimers[streamId].abort();
      aiSumaryTimers[streamId] = null;
      setAISummary(streamId, { error: false, loading: false, result: null });
    }
  }

  function openAIModal(panel: AIPanelType) {
    aiModalPanel.value = panel;

    if (aiModalOpen.value) return;

    ModalService.open("AIModal", { noBackdrop: true });
  }

  function resetAIChatHistoryPagination() {
    aiChatHistoryPagination.value = {
      page: 1,
      pageCount: 0,
      pageSize: CHAT_HISTORY_PAGE_SIZE,
    };

    aiChatHistoryScrollPosition.value = 0;
    aiChatHistoryGroupId.value = null;
  }

  function resetAIInputOptions() {
    aiInput.value = defaultAIInputOptions();
  }

  async function loadUserContext() {
    try {
      if (aiUserContextLoaded.value || !features.has("has_ai_user_context")) {
        return;
      }

      aiUserContextLoading.value = true;

      await AIUserContextService.load();

      aiUserContextEnabled.value = true;
      aiUserContextLoaded.value = true;
    } catch (error) {
      if (ApiClient.isApiError(error) && error.response?.status === 404) {
        aiUserContextEnabled.value = false;
        aiUserContextLoaded.value = true;

        return;
      }

      aiUserContextEnabled.value = false;
    } finally {
      aiUserContextLoading.value = false;
    }
  }

  return {
    abortAISummary,
    aiAssistantPrompt,
    aiBooleanCreatorQueryEditorValue,
    aiChatHistoryGroupId,
    aiChatHistoryPagination,
    aiChatHistoryScrollPosition,
    aiInput,
    aiInputListItems,
    aiModalLeft,
    aiModalMinimise,
    aiModalOpen,
    aiModalPanel,
    aiRegions,
    aiStreamSummary,
    aiSuggestedContactIds,
    aiSuggestedContactsFilter,
    aiSummaries,
    aiSummaryChatHistory,
    aiSummarySelectedTopic,
    aiSummaryStreamId,
    aiUserContextEnabled,
    aiUserContextLoaded,

    aiUserContextLoading,
    getAISummary,
    loadAISummary,
    loadUserContext,
    openAIModal,
    resetAIChatHistoryPagination,
    resetAIInputOptions,
    setAISummary,
  };
});
